// src/app/modules/Incidents/database/IncidentsTable.jsx
import React from "react";
import { Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import Moment from "moment";

// ── Schema compat ─────────────────────────────────────────────────────────────

/**
 * New partner schema uses `category` instead of `type`.
 * Old consumer schema uses `type`.
 */
const getType = (row) => row.category || row.type || 'other';

/**
 * New schema uses `createdAt` (Mongoose timestamps), old uses `created_at`.
 */
const getDate = (row) => row.createdAt || row.created_at;

const getSubject = (row) => {
  // Partner incidents have a subject object
  if (row.subject?.name) return { name: row.subject.name, phone: row.subject.phone || null };
  // Consumer incidents have a populated reporter object
  if (row.reporter?.name) return { name: row.reporter.name, phone: row.reporter.phone || null };
  return null;
};

// ── Status config (old + new) ─────────────────────────────────────────────────

const STATUS_CONFIG = {
  // Old schema
  active:        { pill: 'sety-pill-red',    label: 'Active' },
  investigating: { pill: 'sety-pill-amber',  label: 'Investigating' },
  false_alarm:   { pill: 'sety-pill-grey',   label: 'False Alarm' },
  // New schema
  created:       { pill: 'sety-pill-grey',   label: 'Created' },
  dispatching:   { pill: 'sety-pill-amber',  label: 'Dispatching' },
  assigned:      { pill: 'sety-pill-amber',  label: 'Assigned' },
  responding:    { pill: 'sety-pill-purple', label: 'Responding' },
  on_scene:      { pill: 'sety-pill-purple', label: 'On Scene' },
  escalated:     { pill: 'sety-pill-red',    label: 'Escalated' },
  unresponsive:  { pill: 'sety-pill-red',    label: 'Unresponsive' },
  cancelled:     { pill: 'sety-pill-grey',   label: 'Cancelled' },
  // Shared
  resolved:      { pill: 'sety-pill-green',  label: 'Resolved' },
};

const SEVERITY_PILL = {
  critical: 'sety-pill-red',
  high:     'sety-pill-amber',
  medium:   'sety-pill-purple',
  low:      'sety-pill-grey',
};

const TYPE_ICONS = {
  // New schema categories
  sos:             '🆘',
  vehicle:         '🚗',
  location_threat: '📍',
  // Old schema types
  medical:         '🚑',
  fire:            '🔥',
  crime:           '🚨',
  accident:        '🚗',
  security_threat: '⚠️',
  other:           '📋',
};

// ── Table ─────────────────────────────────────────────────────────────────────

export function IncidentsTable({
  incidents,
  loading,
  onViewDetails,
  currentPage,
  perPage,
  totalCount,
  onPageChange,
}) {
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / (perPage || 10)));

  if (loading && !incidents.length) {
    return <div className="sety-tbl-sub" style={{ padding: 22 }}>Loading…</div>;
  }

  if (!incidents.length) {
    return <div className="sety-tbl-sub" style={{ padding: 22 }}>No incidents found.</div>;
  }

  return (
    <div>
      <div className="sety-tbl-wrap">
        <table className="sety-tbl">
          <thead>
            <tr>
              <th>Incident</th>
              <th>Type / Category</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Reported</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((row) => {
              const id = row.incident_id || row._id;
              const subject = getSubject(row);
              const location = row.location?.address || row.location?.city
                ? [row.location?.address, row.location?.city, row.location?.state].filter(Boolean).join(', ')
                : null;
              const type = getType(row);
              const statusCfg = STATUS_CONFIG[row.status] || { pill: 'sety-pill-grey', label: row.status || '—' };
              const date = getDate(row);

              return (
                <tr key={id}>
                  <td style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{row.title || '—'}</div>
                    {subject && (
                      <div className="sety-tbl-sub" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span>{subject.name}</span>
                        {subject.phone && (
                          <a href={`tel:${subject.phone}`} style={{ color: 'var(--purple-500)' }} onClick={e => e.stopPropagation()}>
                            {subject.phone}
                          </a>
                        )}
                      </div>
                    )}
                    {location && (
                      <div className="sety-tbl-sub">📍 {location}</div>
                    )}
                  </td>
                  <td>
                    <span>{TYPE_ICONS[type] || '📋'}</span>{' '}
                    <span>{type.replace(/_/g, ' ')}</span>
                  </td>
                  <td>
                    <span className={`sety-pill ${SEVERITY_PILL[row.severity] || 'sety-pill-grey'}`}>
                      {row.severity?.toUpperCase() || '—'}
                    </span>
                  </td>
                  <td>
                    <span className={`sety-pill ${statusCfg.pill}`}>{statusCfg.label}</span>
                  </td>
                  <td>
                    {date ? (
                      <>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{Moment(date).format('MMM DD, YYYY')}</div>
                        <div className="sety-tbl-sub">{Moment(date).fromNow()}</div>
                      </>
                    ) : <span className="sety-tbl-sub">—</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {id && (
                      <button
                        className="sety-btn sety-btn-ghost"
                        style={{ padding: '6px 10px' }}
                        onClick={() => onViewDetails(id)}
                        title="View details"
                      >
                        <Eye size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 22px', borderTop: '1px solid var(--ink-100)',
        }}>
          <span className="sety-tbl-sub">Page {currentPage} of {totalPages}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="sety-btn sety-btn-ghost" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
              <ChevronLeft size={14} />Prev
            </button>
            <button className="sety-btn sety-btn-ghost" disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
              Next<ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
